import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BranchProvider } from "@/context/BranchContext";
import { Loader2 } from "lucide-react";
import ThemeProvider from "@/components/ThemeProvider";
import UpdateAvailableBanner from "@/components/UpdateAvailableBanner";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import Placeholder from "@/pages/Placeholder";
import WAChat from "@/pages/WAChat";
import InstagramChat from "@/pages/InstagramChat";
import Contacts from "@/pages/Contacts";
import Tags from "@/pages/Tags";
import Templates from "@/pages/Templates";
import Broadcasting from "@/pages/Broadcasting";
import Reports from "@/pages/Reports";
import MetaBilling from "@/pages/MetaBilling";
import MetaAds from "@/pages/MetaAds";
import BillTracking from "@/pages/BillTracking";
import AutomationSettings from "@/pages/AutomationSettings";
import FlowsList from "@/pages/FlowsList";
import FlowEditor from "@/pages/FlowEditor";

function FullscreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
      <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
    </div>
  );
}

function ProtectedShell() {
  const { user, ready } = useAuth();
  if (!ready) return <FullscreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <BranchProvider>
      <Layout />
    </BranchProvider>
  );
}

function OwnerOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "owner") return <Navigate to="/dashboard" replace />;
  return children;
}

function LoginRoute() {
  const { user, ready } = useAuth();
  if (!ready) return <FullscreenLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Login />;
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route element={<ProtectedShell />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/wa-chat" element={<WAChat />} />
              <Route path="/instagram-chat" element={<InstagramChat />} />
              <Route path="/reports" element={<Reports />} />
              <Route
                path="/meta-billing"
                element={<OwnerOnly><MetaBilling /></OwnerOnly>}
              />
              <Route
                path="/meta-ads"
                element={<OwnerOnly><MetaAds /></OwnerOnly>}
              />
              <Route
                path="/bill-tracking"
                element={<OwnerOnly><BillTracking /></OwnerOnly>}
              />
              <Route
                path="/flows"
                element={<OwnerOnly><FlowsList /></OwnerOnly>}
              />
              <Route
                path="/flows/:id"
                element={<OwnerOnly><FlowEditor /></OwnerOnly>}
              />
              <Route
                path="/contacts"
                element={<OwnerOnly><Contacts /></OwnerOnly>}
              />
              <Route
                path="/tags"
                element={<OwnerOnly><Tags /></OwnerOnly>}
              />
              <Route
                path="/templates"
                element={<OwnerOnly><Templates /></OwnerOnly>}
              />
              <Route
                path="/broadcasts"
                element={<OwnerOnly><Broadcasting /></OwnerOnly>}
              />
              <Route
                path="/automation-settings"
                element={<OwnerOnly><AutomationSettings /></OwnerOnly>}
              />
              <Route
                path="/settings"
                element={<OwnerOnly><Settings /></OwnerOnly>}
              />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <UpdateAvailableBanner />
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;